import { useLazyQuery, useQuery } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { extensionFromName } from "components/Document/shared/util/extensionFromName";
import {
  partitionReviewersAndSigners,
  partitionSubmissionDocuments,
  sortSubmissionDocumentsByType,
} from "components/Submission/helpers/documentHelpers";
import { SignaturePreparationBanner } from "components/Submission/SignaturePreparationBanner";
import { SubmissionCommentStream } from "components/Submission/SubmissionCommentStream";
import { SubmissionDisplayReviewOrSignDocument } from "components/Submission/SubmissionDisplayReviewOrSignDocument";
import { SubmissionDisplayStatic } from "components/Submission/SubmissionDisplayStatic";
import { SubmissionHeaderNavigation } from "components/Submission/SubmissionHeaderNavigation";
import { SubmissionRevisionBanner } from "components/Submission/SubmissionRevisionBanner";
import { isDocumentFormSubmission } from "encamp-shared/src/submissions/wasteSubmissionForms";
import {
  GetSubmissionQuery,
  SignatureStatus,
  SignatureType,
  SubmissionStatus,
} from "generated-graphql/graphql";
import { SubmissionReviewState } from "hooks/submission/reviewStates";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useSubmissionForm } from "hooks/useSubmissionForm";
import {
  useApproveSubmissionDocumentReview,
  useGetSubmission,
  useRejectSubmissionDocumentReview,
} from "hooks/useSubmissionMutations";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { GET_EMBEDDED_DOCUMENT_SIGNING_URL } from "queries/submissions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { buildWasteFacilityUrl } from "util/wasteFacilityUrl";
import { stepperFlagForSubmission } from "util/wasteStepperFlags";

import { getSavedWasteSubmissionsUrl } from "../navigation";
import { pickTransitionErrorMessage } from "./pickTransitionErrorMessage";

type Submission = NonNullable<GetSubmissionQuery["submission"]>;

type ReviewFormValues = {
  reviewFinished: boolean;
  rejectionComment: string;
};

function Title({
  submission,
  reviewState,
  isStaff,
}: {
  submission: Submission;
  reviewState: SubmissionReviewState;
  isStaff: boolean;
}) {
  const titleTextAndToolTip = useMemo(() => {
    if (submission.status === SubmissionStatus.ProcessingSubmission) {
      return {
        title: "Processing Submission",
        tooltip:
          "The submission is being processed by Encamp Staff and will be updated when completed",
      };
    }

    if (
      // Staff never need to review or sign a document
      isStaff ||
      // Nothing to review/sign or review/sign is complete
      reviewState === SubmissionReviewState.NO_DOCUMENTS_ASSIGNED ||
      reviewState === SubmissionReviewState.DOCUMENT_REVIEW_COMPLETED
    ) {
      return {
        title: "View Submission",
        tooltip: null,
      };
    }

    // We display a disabled form when SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS
    // for now we are showing the same title to user for both states
    if (
      reviewState === SubmissionReviewState.SIGNER_CAN_SIGN ||
      reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS
    ) {
      return {
        title: "Sign Submission",
        tooltip:
          "Review the submission, then add your signature and complete approval. If edits are needed, use the comment feature and mark the submission as Needs Edits. \n Once the signature step is completed, Encamp will file the submission with the appropriate regulatory body.",
      };
    } else {
      return {
        title: "Review Submission",
        tooltip:
          "Review the submission, then click Ready for Signature and Complete Review. If edits are needed, use the comment feature and mark the submission as Needs Edits and it will be sent back to Encamp for edits to be made.",
      };
    }
  }, [reviewState, submission, isStaff]);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="h5" component="h1">
        {titleTextAndToolTip.title}
      </Typography>
      {titleTextAndToolTip.tooltip && (
        <Tooltip title={titleTextAndToolTip.tooltip}>
          <InfoOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        </Tooltip>
      )}
    </Stack>
  );
}

export function ReadOnlySubmission() {
  const { tenantId, submissionId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, isStaff } = useCurrentUser();
  const { featureFlags, loading: featureFlagsLoading } = useFeatureFlags();

  const submissionsUrl = getSavedWasteSubmissionsUrl(tenantId ?? "", user?.email);

  const { data, loading, error } = useGetSubmission(submissionId || "", !submissionId);

  const stepperUrl = `/o/${tenantId}/waste/submissions/${submissionId}/stepper`;

  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Submissions", to: submissionsUrl },
    { label: "View Submission" },
  ]);

  // All hooks must be called before any conditional returns
  const alerts = useAlerts();
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState<string | null>(null);
  const [previewDocumentName, setPreviewDocumentName] = useState<string | undefined>();
  const [signingCompleted, setSigningCompleted] = useState(false);
  const hasAutoSelected = useRef(false);
  const [signingDialogOpen, setSigningDialogOpen] = useState(false);

  // Lazy query to fetch document download links for inline preview
  const [getDocumentDownloadLink] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.getDocumentDownloadLink) {
        setPreviewDocumentUrl(data.getDocumentDownloadLink);
      } else {
        alerts.error("Unable to load document preview");
      }
    },
    onError: (error) => {
      console.error("Error fetching document download link:", error);
      alerts.error("Error loading document preview");
    },
  });

  // Separate lazy query for downloading documents via the "open in new tab" button
  const [getDocumentDownloadLinkForNewTab] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.getDocumentDownloadLink) {
        window.open(data.getDocumentDownloadLink, "_blank", "noopener");
      } else {
        alerts.error("Unable to download document");
      }
    },
    onError: (error) => {
      console.error("Error fetching document download link:", error);
      alerts.error("Error downloading document");
    },
  });

  const {
    comments,
    setComments,
    currentUsersSubmissionDocumentReviewId,
    reviewState,
    isReviewerASigner,
  } = useSubmissionForm({
    submissionId: data?.submission?.id || "",
    user,
  });

  const { control, handleSubmit, watch, formState, setValue } = useForm<ReviewFormValues>({
    defaultValues: { reviewFinished: true, rejectionComment: "" },
  });
  const reviewFinished = watch("reviewFinished");
  const rejectionComment = watch("rejectionComment");

  const [approveSubmissionDocumentReview, { loading: approveLoading }] =
    useApproveSubmissionDocumentReview();
  const [rejectSubmissionDocumentReview, { loading: rejectLoading }] =
    useRejectSubmissionDocumentReview();

  const { data: signingUrlData } = useQuery(GET_EMBEDDED_DOCUMENT_SIGNING_URL, {
    variables: { submissionId: data?.submission?.id || "" },
    fetchPolicy: "network-only",
    // We only need to fetch the signing URL if the signer is able to sign
    skip: !data?.submission?.id || reviewState !== SubmissionReviewState.SIGNER_CAN_SIGN,
  });

  const submissionDocumentReviewId = currentUsersSubmissionDocumentReviewId;

  const isMutating = approveLoading || rejectLoading || formState.isSubmitting;

  const handleReviewSubmit = useCallback(
    async (values: ReviewFormValues) => {
      if (!submissionDocumentReviewId) {
        alerts.error("Unable to locate a document review to update.");
        return;
      }

      try {
        if (values.reviewFinished) {
          await approveSubmissionDocumentReview({
            variables: { submissionDocumentReviewId },
          });

          if (reviewState === SubmissionReviewState.SIGNER_CAN_SIGN) {
            alerts.success(
              "Thank you for signing! This status has updated to ✅ Awaiting Submission."
            );
          } else {
            alerts.success("Review completed\nThank you for completing your review!");
          }
        } else {
          const rejectionComment = values.rejectionComment.trim();
          await rejectSubmissionDocumentReview({
            variables: { submissionDocumentReviewId, rejectionComment },
          });
          alerts.success(
            "Review completed\nThank you for completing your review! This submission was sent back to Encamp for edits."
          );
        }
        navigate(submissionsUrl);
      } catch (error) {
        console.error("Failed to complete submission review", error);
        const safeMessage = pickTransitionErrorMessage(error);
        if (safeMessage) {
          alerts.warning(`Your review was saved. ${safeMessage}`);
        } else {
          alerts.error("Unable to complete review. Please try again.");
        }
      }
    },
    [
      alerts,
      navigate,
      approveSubmissionDocumentReview,
      rejectSubmissionDocumentReview,
      submissionDocumentReviewId,
      reviewState,
      submissionsUrl,
    ]
  );

  const handleDocumentPreview = useCallback(
    (document: { id: string; name?: string }) => {
      if (document?.id) {
        setPreviewDocumentName(document.name);
        getDocumentDownloadLink({
          variables: { id: document.id },
        });
      }
    },
    [getDocumentDownloadLink]
  );

  const handleDocumentOpenInNewTab = useCallback(
    (document: { id: string }) => {
      if (document?.id) {
        getDocumentDownloadLinkForNewTab({
          variables: { id: document.id },
        });
      }
    },
    [getDocumentDownloadLinkForNewTab]
  );

  const handleSigningCompleted = useCallback(() => {
    setSigningCompleted(true);
  }, []);

  const handleSigningDialogClose = useCallback(() => {
    setSigningDialogOpen(false);
  }, []);

  const handleSigningDialogOpen = useCallback(() => {
    setSigningDialogOpen(true);
  }, []);

  // Extract signer from reviewers
  // we should probably not assume 1 signer, currently we really only support 1 signer
  const { signer, reviewers } = useMemo(() => {
    if (!data?.submission) {
      return { signer: undefined, reviewers: [] };
    }
    return partitionReviewersAndSigners(data.submission.reviewers || []);
  }, [data?.submission]);

  const facilities = useMemo(() => {
    const facilitySubmissions = data?.submission?.facilitySubmissions ?? [];
    return facilitySubmissions
      .map((fs) => {
        const facility = fs.facility;
        if (!facility) return null;
        const epaAlt = facility.facilityAlternateIds?.find((a) => a.type === "EPA" && !a.hasNoId);
        return {
          display: `${facility.name}${
            facility.customerFacilityId ? ` (${facility.customerFacilityId})` : ""
          } - ${facility.streetAddress1}`,
          epaId: epaAlt?.value ?? null,
          href: tenantId ? buildWasteFacilityUrl(tenantId, facility.id) : null,
        };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null);
  }, [data?.submission, tenantId]);

  // Separate submission documents from attachments and sort by type
  const { submissionDocuments: unsortedSubmissionDocuments, attachmentDocuments } = useMemo(() => {
    if (!data?.submission) {
      return { submissionDocuments: [], attachmentDocuments: [] };
    }
    const docs = (data.submission.documents || []).map((doc) => ({
      id: doc.document.id,
      name: doc.document.title || "Untitled",
      documentType: doc.document.documentType || "PDF",
      submissionDocumentType: doc.type,
      status: doc.status ?? undefined,
      url: doc.document.storageLink,
    }));
    return partitionSubmissionDocuments(docs);
  }, [data?.submission]);

  const submissionDocuments = useMemo(
    () => sortSubmissionDocumentsByType(unsortedSubmissionDocuments),
    [unsortedSubmissionDocuments]
  );

  useEffect(() => {
    if (data?.submission) {
      setComments(data.submission.comments || []);
    }
  }, [data?.submission, setComments]);

  const currentUserReviewer = data?.submission?.reviewers?.find((r) => r.user.id === user?.id);

  useEffect(() => {
    if (!currentUserReviewer) {
      return;
    }

    if (currentUserReviewer.reviewerStatus === "APPROVED") {
      setValue("reviewFinished", true, { shouldDirty: false });
    } else if (currentUserReviewer.reviewerStatus === "REJECTED") {
      setValue("reviewFinished", false, { shouldDirty: false });
    }
    // Recover signingCompleted from DB state so a page reload after signing
    // (but before clicking "Finish") doesn't permanently disable the button.
    if (currentUserReviewer.signatureStatus === SignatureStatus.Completed) {
      setSigningCompleted(true);
    }
  }, [currentUserReviewer, setValue]);

  useEffect(() => {
    if (reviewFinished && rejectionComment.trim().length > 0) {
      setValue("rejectionComment", "", { shouldDirty: false });
    }
  }, [reviewFinished, rejectionComment, setValue]);

  // Auto-select first document on mount
  useEffect(() => {
    // Only auto-select once on initial mount
    if (!hasAutoSelected.current) {
      if (submissionDocuments.length > 0) {
        const firstDoc = submissionDocuments[0];
        if (firstDoc?.id) {
          hasAutoSelected.current = true;
          setPreviewDocumentName(firstDoc.name);
          getDocumentDownloadLink({
            variables: { id: firstDoc.id },
          });
        }
      } else if (attachmentDocuments.length > 0) {
        const firstAttachment = attachmentDocuments[0];
        if (firstAttachment?.id) {
          hasAutoSelected.current = true;
          setPreviewDocumentName(firstAttachment.name);
          getDocumentDownloadLink({
            variables: { id: firstAttachment.id },
          });
        }
      }
    }
  }, [submissionDocuments, attachmentDocuments, getDocumentDownloadLink]);

  // Now we can have conditional returns after all hooks are called
  if (loading || featureFlagsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">Failed to load submission: {error.message}</Alert>
      </Box>
    );
  }

  if (!data?.submission) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="warning">Submission not found</Alert>
      </Box>
    );
  }

  const submission = data.submission;

  // Guided (stepper) submissions are viewed in the stepper, not this
  // upload-oriented detail view. Entry mode is fixed at creation and derived
  // from the presence of SubmissionStep rows (`usesStepper`), so this applies
  // to everyone — staff (who can edit) and customers (read-only), mirroring
  // the Tier II report flow. The governing flag depends on the submission's
  // archetype (interactive vs document form) — the same dispatch the stepper
  // shell's own redirect uses. Only redirect while that flag is on — the shell
  // navigates back here when it's off, so redirecting would loop; with the
  // flag off this detail view is the degraded read-only fallback.
  const stepperEnabled =
    featureFlags[
      stepperFlagForSubmission(submission.submissionType, submission.submissionMethod)
    ] === true;
  // Document forms have no review/sign step in the stepper yet (that flow is a
  // separate ticket) and the review/sign UI lives only on this detail view, so
  // once one reaches review/signing keep it here rather than stranding its
  // reviewers and signers in a stepper where they cannot act. The stepper shell
  // mirrors this, redirecting the same submissions back here.
  const isDocumentFormInReview =
    isDocumentFormSubmission(submission.submissionType, submission.submissionMethod) &&
    submission.status === SubmissionStatus.InReview;
  if (submission.usesStepper && stepperEnabled && !isDocumentFormInReview) {
    return <Navigate to={stepperUrl} replace />;
  }

  const handleEditSubmission = () => {
    navigate(`/o/${tenantId}/waste/submissions/${submissionId}/edit`);
  };

  const isDigitalSigner = isReviewerASigner && submission.signatureType === SignatureType.Digital;

  const showReviewOrSignPanel =
    !(isDigitalSigner && signingCompleted) &&
    (reviewState === SubmissionReviewState.SIGNER_CAN_SIGN ||
      reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS ||
      (reviewState === SubmissionReviewState.REVIEWER_CAN_REVIEW && !isDigitalSigner));

  return (
    <Stack>
      {tenantId && <SubmissionHeaderNavigation tenantId={tenantId} />}

      {/* Main Content Area - matches index page background */}
      <Stack
        id="submission-view"
        p={2}
        sx={{
          background:
            theme.palette.mode === "light" ? theme.palette.grey50.light : theme.palette.grey50.dark,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Box
            sx={{
              p: 3,
              pb: 0,
            }}
          >
            {/* Header Section */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Title isStaff={isStaff} reviewState={reviewState} submission={submission} />

              {isStaff && (
                <Stack direction="row" spacing={1}>
                  <Button
                    component="a"
                    href={`/staff/fulfillment/waste/submissions/${submissionId}`}
                    target="_blank"
                    variant="outlined"
                    color="warning"
                    endIcon={<OpenInNewIcon />}
                  >
                    Open Fulfillment
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEditSubmission}
                    sx={{
                      backgroundColor: "warning.main",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "warning.dark",
                      },
                    }}
                  >
                    Edit Submission
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>

          <SubmissionRevisionBanner
            tenantId={tenantId ?? ""}
            revisedFromSubmission={submission.revisedFromSubmission}
            revisionSubmissions={submission.revisionSubmissions}
            sx={{ mx: 3 }}
          />

          <SignaturePreparationBanner submission={submission} sx={{ mx: 3 }} />

          {/* Submission Content */}
          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              <SubmissionDisplayStatic
                submission={submission}
                reviewState={reviewState}
                facilities={facilities}
                reviewers={reviewers}
                signer={signer}
                submissionDocuments={submissionDocuments}
                attachmentDocuments={attachmentDocuments}
                previewDocumentUrl={previewDocumentUrl}
                previewDocumentExtension={extensionFromName(previewDocumentName)}
                signingCompleted={signingCompleted}
                signingDialogOpen={signingDialogOpen}
                signingUrlData={signingUrlData}
                tenantId={tenantId ?? ""}
                isStaff={isStaff}
                onDocumentPreview={handleDocumentPreview}
                onDocumentOpenInNewTab={handleDocumentOpenInNewTab}
                onSigningDialogClose={handleSigningDialogClose}
                onSigningDialogOpen={handleSigningDialogOpen}
                onSigningCompleted={handleSigningCompleted}
              />

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <SubmissionCommentStream
                    submissionId={submission.id}
                    comments={comments}
                    commentAdded={(comment) => {
                      setComments((prev) => [...prev, comment]);
                    }}
                    commentDeleted={(commentId) => {
                      setComments((prev) => prev.filter((comment) => comment.id !== commentId));
                    }}
                  />
                </Grid>

                {showReviewOrSignPanel && (
                  <SubmissionDisplayReviewOrSignDocument
                    isReviewerASigner={isReviewerASigner}
                    isCurrentUserStaff={isStaff}
                    hasSigner={!!signer}
                    hasCustomerReviewers={reviewers.some((r) => !r.isStaff)}
                    hasOtherPendingReviewers={reviewers.some(
                      (r) => r.user.id !== user?.id && !r.completedAt
                    )}
                    reviewState={reviewState}
                    reviewFinished={reviewFinished}
                    rejectionComment={rejectionComment}
                    submissionDocumentReviewId={submissionDocumentReviewId}
                    submissionStatus={submission.status}
                    signatureType={submission.signatureType}
                    signingCompleted={signingCompleted}
                    isMutating={isMutating}
                    control={control}
                    errors={formState.errors}
                    onSubmit={handleSubmit(handleReviewSubmit)}
                  />
                )}
              </Grid>
            </Stack>
          </Box>
        </Paper>
      </Stack>
    </Stack>
  );
}
